/**
 * Translation dictionaries for the customer app.
 * Supported locales: ko (default), en, ja, zh.
 *
 * All keys must exist in every locale to keep fallback logic simple.
 * Use flat dot-separated keys (e.g. `nav.menu`) for organization.
 */

const translations = {
  ko: {
    // Common
    'common.appName': '스마트 오더',
    'common.confirm': '확인',
    'common.cancel': '취소',
    'common.close': '닫기',
    'common.next': '다음',
    'common.prev': '이전',
    'common.back': '뒤로',
    'common.save': '저장',
    'common.delete': '삭제',
    'common.edit': '수정',
    'common.add': '추가',
    'common.remove': '제거',
    'common.yes': '예',
    'common.no': '아니오',
    'common.submit': '전송',
    'common.retry': '다시 시도',
    'common.loading': '불러오는 중...',
    'common.error': '오류가 발생했습니다',
    'common.success': '완료',
    'common.fail': '실패',
    'common.required': '필수',
    'common.optional': '선택',
    'common.search': '검색',
    'common.all': '전체',

    // Navigation
    'nav.menu': '메뉴',
    'nav.cart': '장바구니',
    'nav.orders': '주문내역',
    'nav.staffCall': '직원 호출',
    'nav.home': '홈',

    // Menu
    'menu.title': '메뉴',
    'menu.recommended': '추천 메뉴',
    'menu.popular': '인기',
    'menu.new': '신메뉴',
    'menu.soldOut': '품절',
    'menu.empty': '메뉴가 없습니다',
    'menu.emptyHint': '다른 카테고리를 선택해 보세요',
    'menu.options': '옵션',
    'menu.extraOptions': '추가 옵션',
    'menu.free': '무료',
    'menu.addToCart': '장바구니 담기',
    'menu.soldOutNotice': '품절된 메뉴입니다',
    'menu.description': '설명',
    'menu.price': '가격',

    // Cart
    'cart.title': '장바구니',
    'cart.empty': '장바구니가 비어있습니다',
    'cart.quantity': '수량',
    'cart.subtotal': '소계',
    'cart.total': '합계',
    'cart.checkout': '주문하기',
    'cart.viewCart': '장바구니 보기',
    'cart.addedToast': '담았어요!',
    'cart.removeItem': '삭제',
    'cart.clearAll': '전체 삭제',
    'cart.itemCount': '개',

    // Order
    'order.title': '주문',
    'order.placed': '주문이 접수되었습니다',
    'order.pending': '주문 대기 중',
    'order.accepted': '주문 확인',
    'order.cooking': '조리 중',
    'order.served': '서빙 완료',
    'order.cancelled': '취소됨',
    'order.status': '주문 상태',
    'order.number': '주문 번호',
    'order.time': '주문 시각',
    'order.history': '주문 내역',
    'order.refund': '환불 요청',
    'order.cancel': '주문 취소',

    // Payment
    'payment.title': '결제',
    'payment.method': '결제 수단',
    'payment.card': '카드결제',
    'payment.kakaoPay': '카카오페이',
    'payment.tossPay': '토스페이',
    'payment.selfCheckout': '셀프 계산하기',
    'payment.dutchPay': '더치페이',
    'payment.receipt': '영수증',
    'payment.cashReceipt': '현금영수증',
    'payment.amount': '결제 금액',
    'payment.pay': '결제하기',
    'payment.success': '결제 완료',
    'payment.fail': '결제 실패',
    'payment.processing': '결제 처리 중...',

    // Staff call
    'staff.title': '직원 호출',
    'staff.call': '직원 부르기',
    'staff.water': '물 요청',
    'staff.utensils': '수저 요청',
    'staff.cleanup': '테이블 정리',
    'staff.other': '기타 요청',
    'staff.message': '메시지',
    'staff.messagePlaceholder': '자세한 요청사항을 입력하세요',
    'staff.send': '호출하기',
    'staff.called': '호출 완료',
    'staff.failed': '호출에 실패했습니다',

    // Store / Hours
    'store.closed': '영업 종료',
    'store.open': '영업 중',
    'store.hours': '운영시간',
    'store.closedNotice': '현재 영업시간이 아닙니다',
    'store.table': '테이블',
    'store.tableNumber': '테이블 {number}번',

    // Refund
    'refund.title': '환불',
    'refund.request': '환불 요청',
    'refund.reason': '환불 사유',
    'refund.confirm': '환불 확인',
    'refund.processing': '환불 처리 중',

    // Legal
    'legal.terms': '이용약관',
    'legal.privacy': '개인정보처리방침',
    'legal.refundPolicy': '환불정책',
    'legal.support': '고객지원',
    'legal.businessInfo': '사업자정보',
    'legal.ceo': '대표자',
    'legal.businessNumber': '사업자등록번호',
    'legal.address': '사업장 주소',
    'legal.mailOrder': '통신판매업 신고번호',

    // Language
    'lang.title': '언어 선택',
    'lang.ko': '한국어',
    'lang.en': 'English',
    'lang.ja': '日本語',
    'lang.zh': '中文',
  },

  en: {
    'common.appName': 'Smart Order',
    'common.confirm': 'Confirm',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.next': 'Next',
    'common.prev': 'Previous',
    'common.back': 'Back',
    'common.save': 'Save',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.remove': 'Remove',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.submit': 'Submit',
    'common.retry': 'Retry',
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.success': 'Success',
    'common.fail': 'Failed',
    'common.required': 'Required',
    'common.optional': 'Optional',
    'common.search': 'Search',
    'common.all': 'All',

    'nav.menu': 'Menu',
    'nav.cart': 'Cart',
    'nav.orders': 'Orders',
    'nav.staffCall': 'Call Staff',
    'nav.home': 'Home',

    'menu.title': 'Menu',
    'menu.recommended': 'Recommended',
    'menu.popular': 'Popular',
    'menu.new': 'New',
    'menu.soldOut': 'Sold Out',
    'menu.empty': 'No menu items',
    'menu.emptyHint': 'Try another category',
    'menu.options': 'Options',
    'menu.extraOptions': 'Add-ons',
    'menu.free': 'Free',
    'menu.addToCart': 'Add to Cart',
    'menu.soldOutNotice': 'This item is sold out',
    'menu.description': 'Description',
    'menu.price': 'Price',

    'cart.title': 'Cart',
    'cart.empty': 'Your cart is empty',
    'cart.quantity': 'Quantity',
    'cart.subtotal': 'Subtotal',
    'cart.total': 'Total',
    'cart.checkout': 'Checkout',
    'cart.viewCart': 'View Cart',
    'cart.addedToast': 'Added!',
    'cart.removeItem': 'Remove',
    'cart.clearAll': 'Clear All',
    'cart.itemCount': 'items',

    'order.title': 'Order',
    'order.placed': 'Your order has been placed',
    'order.pending': 'Pending',
    'order.accepted': 'Accepted',
    'order.cooking': 'Cooking',
    'order.served': 'Served',
    'order.cancelled': 'Cancelled',
    'order.status': 'Order Status',
    'order.number': 'Order Number',
    'order.time': 'Ordered At',
    'order.history': 'Order History',
    'order.refund': 'Request Refund',
    'order.cancel': 'Cancel Order',

    'payment.title': 'Payment',
    'payment.method': 'Payment Method',
    'payment.card': 'Credit Card',
    'payment.kakaoPay': 'KakaoPay',
    'payment.tossPay': 'TossPay',
    'payment.selfCheckout': 'Self Checkout',
    'payment.dutchPay': 'Split Bill',
    'payment.receipt': 'Receipt',
    'payment.cashReceipt': 'Cash Receipt',
    'payment.amount': 'Amount',
    'payment.pay': 'Pay Now',
    'payment.success': 'Payment Successful',
    'payment.fail': 'Payment Failed',
    'payment.processing': 'Processing payment...',

    'staff.title': 'Call Staff',
    'staff.call': 'Call Staff',
    'staff.water': 'Water',
    'staff.utensils': 'Utensils',
    'staff.cleanup': 'Clean Table',
    'staff.other': 'Other',
    'staff.message': 'Message',
    'staff.messagePlaceholder': 'Enter details of your request',
    'staff.send': 'Call',
    'staff.called': 'Staff called',
    'staff.failed': 'Failed to call staff',

    'store.closed': 'Closed',
    'store.open': 'Open',
    'store.hours': 'Hours',
    'store.closedNotice': 'We are currently closed',
    'store.table': 'Table',
    'store.tableNumber': 'Table {number}',

    'refund.title': 'Refund',
    'refund.request': 'Request Refund',
    'refund.reason': 'Reason',
    'refund.confirm': 'Confirm Refund',
    'refund.processing': 'Processing refund',

    'legal.terms': 'Terms of Service',
    'legal.privacy': 'Privacy Policy',
    'legal.refundPolicy': 'Refund Policy',
    'legal.support': 'Support',
    'legal.businessInfo': 'Business Info',
    'legal.ceo': 'CEO',
    'legal.businessNumber': 'Business No.',
    'legal.address': 'Address',
    'legal.mailOrder': 'Mail Order Reg. No.',

    'lang.title': 'Select Language',
    'lang.ko': '한국어',
    'lang.en': 'English',
    'lang.ja': '日本語',
    'lang.zh': '中文',
  },

  ja: {
    'common.appName': 'スマートオーダー',
    'common.confirm': '確認',
    'common.cancel': 'キャンセル',
    'common.close': '閉じる',
    'common.next': '次へ',
    'common.prev': '前へ',
    'common.back': '戻る',
    'common.save': '保存',
    'common.delete': '削除',
    'common.edit': '編集',
    'common.add': '追加',
    'common.remove': '削除',
    'common.yes': 'はい',
    'common.no': 'いいえ',
    'common.submit': '送信',
    'common.retry': '再試行',
    'common.loading': '読み込み中...',
    'common.error': 'エラーが発生しました',
    'common.success': '成功',
    'common.fail': '失敗',
    'common.required': '必須',
    'common.optional': '任意',
    'common.search': '検索',
    'common.all': 'すべて',

    'nav.menu': 'メニュー',
    'nav.cart': 'カート',
    'nav.orders': '注文履歴',
    'nav.staffCall': 'スタッフ呼出',
    'nav.home': 'ホーム',

    'menu.title': 'メニュー',
    'menu.recommended': 'おすすめ',
    'menu.popular': '人気',
    'menu.new': '新メニュー',
    'menu.soldOut': '売り切れ',
    'menu.empty': 'メニューがありません',
    'menu.emptyHint': '他のカテゴリをお試しください',
    'menu.options': 'オプション',
    'menu.extraOptions': '追加オプション',
    'menu.free': '無料',
    'menu.addToCart': 'カートに追加',
    'menu.soldOutNotice': 'このメニューは売り切れです',
    'menu.description': '説明',
    'menu.price': '価格',

    'cart.title': 'カート',
    'cart.empty': 'カートは空です',
    'cart.quantity': '数量',
    'cart.subtotal': '小計',
    'cart.total': '合計',
    'cart.checkout': '注文する',
    'cart.viewCart': 'カートを見る',
    'cart.addedToast': '追加しました!',
    'cart.removeItem': '削除',
    'cart.clearAll': 'すべて削除',
    'cart.itemCount': '点',

    'order.title': '注文',
    'order.placed': 'ご注文を承りました',
    'order.pending': '受付中',
    'order.accepted': '確認済み',
    'order.cooking': '調理中',
    'order.served': '提供済み',
    'order.cancelled': 'キャンセル済み',
    'order.status': '注文状況',
    'order.number': '注文番号',
    'order.time': '注文時刻',
    'order.history': '注文履歴',
    'order.refund': '返金リクエスト',
    'order.cancel': '注文キャンセル',

    'payment.title': 'お支払い',
    'payment.method': '支払い方法',
    'payment.card': 'クレジットカード',
    'payment.kakaoPay': 'カカオペイ',
    'payment.tossPay': 'トスペイ',
    'payment.selfCheckout': 'セルフ会計',
    'payment.dutchPay': '割り勘',
    'payment.receipt': 'レシート',
    'payment.cashReceipt': '現金領収証',
    'payment.amount': '金額',
    'payment.pay': '支払う',
    'payment.success': '支払い完了',
    'payment.fail': '支払い失敗',
    'payment.processing': '処理中...',

    'staff.title': 'スタッフ呼出',
    'staff.call': 'スタッフを呼ぶ',
    'staff.water': 'お水',
    'staff.utensils': 'カトラリー',
    'staff.cleanup': 'テーブル清掃',
    'staff.other': 'その他',
    'staff.message': 'メッセージ',
    'staff.messagePlaceholder': 'ご要望の詳細を入力',
    'staff.send': '呼び出す',
    'staff.called': '呼び出しました',
    'staff.failed': '呼び出しに失敗しました',

    'store.closed': '営業終了',
    'store.open': '営業中',
    'store.hours': '営業時間',
    'store.closedNotice': '現在営業時間外です',
    'store.table': 'テーブル',
    'store.tableNumber': 'テーブル {number}',

    'refund.title': '返金',
    'refund.request': '返金リクエスト',
    'refund.reason': '理由',
    'refund.confirm': '返金確認',
    'refund.processing': '返金処理中',

    'legal.terms': '利用規約',
    'legal.privacy': 'プライバシーポリシー',
    'legal.refundPolicy': '返金ポリシー',
    'legal.support': 'サポート',
    'legal.businessInfo': '事業者情報',
    'legal.ceo': '代表者',
    'legal.businessNumber': '事業者登録番号',
    'legal.address': '所在地',
    'legal.mailOrder': '通信販売業届出番号',

    'lang.title': '言語を選択',
    'lang.ko': '한국어',
    'lang.en': 'English',
    'lang.ja': '日本語',
    'lang.zh': '中文',
  },

  zh: {
    'common.appName': '智能点餐',
    'common.confirm': '确认',
    'common.cancel': '取消',
    'common.close': '关闭',
    'common.next': '下一步',
    'common.prev': '上一步',
    'common.back': '返回',
    'common.save': '保存',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.add': '添加',
    'common.remove': '移除',
    'common.yes': '是',
    'common.no': '否',
    'common.submit': '提交',
    'common.retry': '重试',
    'common.loading': '加载中...',
    'common.error': '发生错误',
    'common.success': '成功',
    'common.fail': '失败',
    'common.required': '必填',
    'common.optional': '可选',
    'common.search': '搜索',
    'common.all': '全部',

    'nav.menu': '菜单',
    'nav.cart': '购物车',
    'nav.orders': '订单',
    'nav.staffCall': '呼叫服务员',
    'nav.home': '首页',

    'menu.title': '菜单',
    'menu.recommended': '推荐',
    'menu.popular': '热门',
    'menu.new': '新菜品',
    'menu.soldOut': '售罄',
    'menu.empty': '暂无菜品',
    'menu.emptyHint': '请尝试其他分类',
    'menu.options': '选项',
    'menu.extraOptions': '附加选项',
    'menu.free': '免费',
    'menu.addToCart': '加入购物车',
    'menu.soldOutNotice': '该菜品已售罄',
    'menu.description': '描述',
    'menu.price': '价格',

    'cart.title': '购物车',
    'cart.empty': '购物车是空的',
    'cart.quantity': '数量',
    'cart.subtotal': '小计',
    'cart.total': '合计',
    'cart.checkout': '结账',
    'cart.viewCart': '查看购物车',
    'cart.addedToast': '已添加!',
    'cart.removeItem': '删除',
    'cart.clearAll': '清空',
    'cart.itemCount': '件',

    'order.title': '订单',
    'order.placed': '订单已提交',
    'order.pending': '等待确认',
    'order.accepted': '已确认',
    'order.cooking': '制作中',
    'order.served': '已上菜',
    'order.cancelled': '已取消',
    'order.status': '订单状态',
    'order.number': '订单号',
    'order.time': '下单时间',
    'order.history': '订单记录',
    'order.refund': '申请退款',
    'order.cancel': '取消订单',

    'payment.title': '支付',
    'payment.method': '支付方式',
    'payment.card': '信用卡',
    'payment.kakaoPay': 'KakaoPay',
    'payment.tossPay': 'TossPay',
    'payment.selfCheckout': '自助结账',
    'payment.dutchPay': 'AA制',
    'payment.receipt': '收据',
    'payment.cashReceipt': '现金收据',
    'payment.amount': '金额',
    'payment.pay': '支付',
    'payment.success': '支付成功',
    'payment.fail': '支付失败',
    'payment.processing': '处理中...',

    'staff.title': '呼叫服务员',
    'staff.call': '呼叫服务员',
    'staff.water': '加水',
    'staff.utensils': '餐具',
    'staff.cleanup': '清理桌面',
    'staff.other': '其他',
    'staff.message': '留言',
    'staff.messagePlaceholder': '请输入详细要求',
    'staff.send': '呼叫',
    'staff.called': '已呼叫',
    'staff.failed': '呼叫失败',

    'store.closed': '已打烊',
    'store.open': '营业中',
    'store.hours': '营业时间',
    'store.closedNotice': '当前不在营业时间',
    'store.table': '桌号',
    'store.tableNumber': '桌号 {number}',

    'refund.title': '退款',
    'refund.request': '申请退款',
    'refund.reason': '原因',
    'refund.confirm': '确认退款',
    'refund.processing': '正在处理退款',

    'legal.terms': '服务条款',
    'legal.privacy': '隐私政策',
    'legal.refundPolicy': '退款政策',
    'legal.support': '客户支持',
    'legal.businessInfo': '商家信息',
    'legal.ceo': '代表',
    'legal.businessNumber': '营业执照号',
    'legal.address': '地址',
    'legal.mailOrder': '通信销售登记号',

    'lang.title': '选择语言',
    'lang.ko': '한국어',
    'lang.en': 'English',
    'lang.ja': '日本語',
    'lang.zh': '中文',
  },
};

export default translations;
